import L from 'leaflet';
import 'leaflet-indoor-pytek';
import turf from '@turf/turf';
import { get, isEmpty, toString } from 'lodash';

import * as layerOps from '../../api/Layers/layer-operations';
import * as styleOps from '../../api/Styles/style-operations';

import adaptURL from '../adapt-url';
import customLevelControl from './customLevelControl';
import { getGeoJSONStyle } from '../geojson-layer-style';
import loadGeoJSONFromURL from '../load-geojson-from-url';
import loadStyleFromURL from '../load-style-from-url';

/**
 * Builds the Indoor GeoJSON layer.
 *
 * @async
 * @param {object} arg - The object argument.
 * @param {object} arg.layer - The layer object.
 * @param {object} arg.style - The style object.
 * @param {object} arg.leafletMap - The leaflet map object.
 * @param {boolean} [arg.isPartOfMap=false] - Flag that is true if layer is part of a map.
 * @param {number} [arg.zIndex=400] - The zIndex for the layer.
 * @param {boolean} [arg.zoomLevel=false] - A flag to check if zoom should be checked.
 * @returns {Promise<object>} The leaflet layer to add to the map with the url and the options.
 */
const buildIndoorGeoJSONLayer = async ({
  layer,
  style,
  isPartOfMap = false,
  zIndex = 400,
  leafletMap,
  zoomLevel = false
}) => {
  let geojsonFile = '';
  let layerToDraw = '';
  let styleOptions;
  let options;
  let customStyleObject;
  try {
    const url = adaptURL(layerOps.getURL(layer));
    geojsonFile = await loadGeoJSONFromURL(url);
    if (styleOps.isCustom(style)) {
      customStyleObject = await loadStyleFromURL({
        url: adaptURL(styleOps.getStyleURL(style)),
        layer
      });
    }
    if (isPartOfMap) {
      styleOptions = await getGeoJSONStyle({
        style,
        layer,
        pane: toString(zIndex),
        customStyleObject
      });
      options = {
        ...styleOptions,
        attribution: layerOps.getAttribution(layer),
        pane: toString(zIndex)
      };
    } else {
      styleOptions = await getGeoJSONStyle({ style, layer, customStyleObject });
      options = { ...styleOptions, attribution: layerOps.getAttribution(layer) };
    }

    layerToDraw = L.geoJSON(geojsonFile, options);

    let subLayerToDraw = {};
    let levelControl = {};
    let activeMainLayer = true;
    turf.featureEach(geojsonFile, async feature => {
      const subLayerURL = adaptURL(get(feature, 'properties.url', ''));

      if (!isEmpty(subLayerURL)) {
        const center = turf.centerOfMass(feature);
        const coords = [center.geometry.coordinates[1], center.geometry.coordinates[0]];
        const marker = L.marker(coords).addTo(leafletMap);

        layerToDraw.on('add', () => {
          marker.addTo(leafletMap);
          leafletMap.removeLayer(subLayerToDraw);
        });
        layerToDraw.on('remove', () => leafletMap.removeLayer(marker));

        const subGeoJSONFile = await loadGeoJSONFromURL(subLayerURL);

        subLayerToDraw = new L.Indoor(subGeoJSONFile, {
          getLevel: subFeature => {
            if (subFeature.properties[layerOps.getIndoorLevel(layer)] === -1) return null;
            return subFeature.properties[layerOps.getIndoorLevel(layer)];
          },
          ...options
        });
        let deafultLevel = '0';
        turf.featureEach(subGeoJSONFile, (subFeature, index) => {
          if (index === 0)
            deafultLevel = `${get(subFeature, ['properties', layerOps.getIndoorLevel(layer)], 0)}`;
        });
        subLayerToDraw.setLevel(deafultLevel);

        levelControl = customLevelControl({
          level: deafultLevel,
          levels: subLayerToDraw.getLevels(),
          indoorLayer: subLayerToDraw
        });

        subLayerToDraw.on('add', () => {
          levelControl.addTo(leafletMap);
          leafletMap.removeLayer(layerToDraw);
        });
        subLayerToDraw.on('remove', () => leafletMap.removeControl(levelControl));

        marker.on('click', () => {
          const zoom = leafletMap.getZoom();
          if (!zoomLevel || layerOps.isZoomValid(layer, zoom)) {
            subLayerToDraw.addTo(leafletMap);
            activeMainLayer = false;
          }
        });

        leafletMap.on('click', event => {
          const zoom = event.target.getZoom();
          event.originalEvent.preventDefault();
          if (!isEmpty(subLayerToDraw) && (!zoomLevel || layerOps.isZoomValid(layer, zoom))) {
            layerToDraw.addTo(leafletMap);
            activeMainLayer = true;
          }
        });

        if (zoomLevel) {
          leafletMap.on('zoomend', event => {
            const zoom = event.target.getZoom();
            if (!activeMainLayer && !layerOps.isZoomValid(layer, zoom))
              leafletMap.removeLayer(subLayerToDraw);
            else if (!activeMainLayer && !leafletMap.hasLayer(subLayerToDraw))
              subLayerToDraw.addTo(leafletMap);
          });
        }
      }
    });

    if (zoomLevel) {
      leafletMap.on('zoomend', event => {
        const zoom = event.target.getZoom();
        if (activeMainLayer && !layerOps.isZoomValid(layer, zoom))
          leafletMap.removeLayer(layerToDraw);
        else if (activeMainLayer && !leafletMap.hasLayer(layerToDraw))
          layerToDraw.addTo(leafletMap);
      });
    }

    return layerToDraw;
  } catch (error) {
    console.error(error);
  }
  return null;
};

export default buildIndoorGeoJSONLayer;
